import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router";
import { valueToString } from "helpers/Helpers";
import Page from "components/page/Page";
import { StatusCode } from "api/protocols";
import { GetAccount, GetAccountTransactions, ReopenHouseAccount } from "api/rpc/2024-04/facilityAdmin/client/accounts";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Badge } from "components/badge/Badge";
import Popup from "components/popup/Popup";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import {
  DownloadCustomerAccountStatements,
  EmailCustomerAccountStatement,
  GetAccountTransactionReceipt,
  GetCustomerStatements,
} from "api/rpc/2024-04/facilityAdmin/customer/customer";
import { LocaleCurrency } from "helpers/Locale";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDownLeft, faArrowUpLeft } from "@fortawesome/pro-solid-svg-icons";
import {
  faCreditCard,
  faDollarSign,
  faHashtag,
  faHouse,
  faPen,
} from "@fortawesome/pro-regular-svg-icons";
import Tabs from "components/tabs/Tabs";
import DataTable from "components/dataTable/DataTable";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import useModal from "hooks/modals/useModal";
import Portal from "elements/Portal";
import Sheet from "components/sheet/Sheet";
import Input from "components/form/input";
import {
  HouseAccountStatementType,
  HouseAccountTransactionType,
  HouseAccountType,
  TransactionKind,
} from "../../customer/context/contextTypes";
import { ICustomer } from "redux/reducers/models/customer";
import axios, { CancelToken } from "axios";
import AccountCustomersCell from "./AccountCustomersCell";

import "../../customer/tabs/houseAccounts/CustomerHouseAccounts.scss";
import "./accounts.scss";
import "./AccountOverview.scss";

const TABLE_LIMIT = 50;
const OVERVIEW_VISIBLE_CUSTOMER_NAMES = 4;

interface IAccountState {
  id: number;
  title: string;
  customer_name: string;
  customer_email: string;
  customers: ICustomer[];
  account_number: string;
  account_balance: number;
  currency: string;
  limit: number;
  account_transactions: HouseAccountTransactionType[];
  account_statements: HouseAccountStatementType[] | null;
  closed?: boolean;
  reopenVisible: boolean;
}

export default function AccountOverview() {
  const { accountId } = useParams<{ accountId: string }>();
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { facilityStore } = useAppSelector(store => store);
  const backToAccountsUrl = `/admin/settings/accounts${location.search || ""}`;

  const [accountState, setAccountState] = useState<IAccountState>({
    id: null,
    title: "",
    customer_name: "",
    customer_email: "",
    customers: [],
    account_number: "",
    account_balance: null,
    currency: "",
    limit: null,
    account_transactions: [],
    account_statements: null,
    closed: false,
    reopenVisible: false,
  });
  const [selectedTableTab, setSelectedTableTab] = useState<number>(0);
  const [tableOffset, setTableOffset] = useState(0);
  const [selectedParentId, setSelectedParentId] = useState<number>(null);

  const {
    state: emailStatementModal,
    updateModal: updateEmailStatementModal,
    closeModal: closeEmailStatementModal,
  } = useModal<{ email: string; statementId: number }>({ email: "", statementId: null });

  const tableTabs = [
    { id: "statements", content: t("Statements") },
    { id: "transactions", content: t("Transactions") },
  ];

  const paginatedTransactions = useMemo(() => {
    return accountState.account_transactions.slice(tableOffset, tableOffset + TABLE_LIMIT);
  }, [accountState.account_transactions, tableOffset]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadAccount();
    void loadAccountTransactions(source.token);
    void loadAccountStatements(source.token);

    return () => {
      source.cancel("AccountOverview unmounted.");
    };
  }, [accountId]);

  async function loadAccount() {
    const accountResponse = await GetAccount({ id: Number(accountId) });

    if (
      accountResponse.status !== StatusCode.OK ||
      accountResponse?.data?.length === undefined ||
      accountResponse.data.length === 0
    ) {
      return;
    }

    const account = accountResponse.data[0] as HouseAccountType;
    const limit = account?.limit?.toString() ?? "";
    const customers =
      account.customers && account.customers.length > 0
        ? account.customers
        : account.customer
          ? [account.customer]
          : [];

    setAccountState(prevState => ({
      ...prevState,
      id: isNaN(parseInt(accountId)) ? 0 : parseInt(accountId),
      title: account?.title?.toString() ?? "",
      customer_name: account?.customer?.full_name?.toString() ?? "",
      customer_email: account?.customer?.email?.toString() ?? "",
      customers,
      account_number: account?.account_number?.toString() ?? "",
      account_balance: account.balance,
      currency: account.currency,
      limit: limit,
      closed: account.closed,
    }));
  }

  async function loadAccountTransactions(token?: CancelToken) {
    const accountTransactionsRes = await GetAccountTransactions(
      { account_id: Number(accountId) },
      !token,
      token,
    );

    if (token?.reason) {
      return;
    }

    if (accountTransactionsRes.status !== StatusCode.OK) {
      return;
    }

    const accountTransactions = Array.isArray(accountTransactionsRes.data) ? accountTransactionsRes.data : [];
    const filteredTransactions = (accountTransactions as HouseAccountTransactionType[]).filter(
      transaction => transaction?.kind !== "authorization",
    );
    const markRefunds = determineRefundedTransactions(filteredTransactions);
    const facilities = facilityStore.facilities ?? [];

    for (let i = 0; i < markRefunds.length; i++) {
      for (let k = 0; k < facilities.length; k++) {
        if (markRefunds[i].facility_id === facilities[k].id) {
          markRefunds[i].facility = facilities[k];
        }
      }
    }

    setAccountState(prevState => ({
      ...prevState,
      account_transactions: markRefunds,
    }));
    setTableOffset(0);
  }

  async function loadAccountStatements(token?: CancelToken) {
    setAccountState(prevState => ({ ...prevState, account_statements: null }));

    const statementRes = await GetCustomerStatements({ account_id: Number(accountId) }, !token, token);

    if (token?.reason) {
      return;
    }

    if (statementRes.status !== StatusCode.OK) {
      setAccountState(prevState => ({ ...prevState, account_statements: [] }));
      return;
    }

    setAccountState(prevState => ({
      ...prevState,
      account_statements: Array.isArray(statementRes.data) ? statementRes.data : [],
    }));
  }

  function navigateToAccountEdit() {
    history.push({
      pathname: "/admin/settings/accounts/edit/" + String(accountId),
      search: location.search,
    });
  }

  function handleTableNavigation(direction: "prev" | "next") {
    setTableOffset(prev => (direction === "prev" ? Math.max(0, prev - TABLE_LIMIT) : prev + TABLE_LIMIT));
  }

  async function downloadAccountStatement(statementId: number) {
    const downloadRes = await DownloadCustomerAccountStatements({ account_statement_id: statementId }, true);
    if (downloadRes.status !== StatusCode.OK) {
      dispatch(showError(typeof downloadRes.data === "string" ? downloadRes.data : t("Error downloading statement")));
      return;
    }

    window.open().document.write(downloadRes.data as string);
  }

  async function emailAccountStatement(statementId: number) {
    const res = await EmailCustomerAccountStatement(
      {
        id: statementId,
        account_id: Number(accountId),
        email: emailStatementModal?.email ?? undefined,
      },
      true,
    );

    if (res?.status !== StatusCode.OK) {
      dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error emailing statement")));
      return;
    }

    dispatch(showSuccess(t("Statement email sent successfully")));
    void closeEmailStatementModal();
  }

  async function getAccountTransactionReceipt(transactionId: number) {
    const receiptRes = await GetAccountTransactionReceipt({ account_transaction_id: transactionId }, false);
    if (receiptRes.status !== StatusCode.OK) {
      dispatch(showError(typeof receiptRes.data === "string" ? receiptRes.data : t("Error retrieving receipt")));
      return;
    }
    window.open().document.write(receiptRes.data as string);
  }

  const primaryAction = {
    content: (
      <>
        <FontAwesomeIcon icon={faPen} />
        <span className="ml-2">{t("Edit account")}</span>
      </>
    ),
    action: navigateToAccountEdit,
  };

  const secondaryActions = {
    content: "Reopen",
    action: () => setAccountState(prevState => ({ ...prevState, reopenVisible: true })),
  };

  const customersAccount: HouseAccountType = {
    id: accountState.id,
    title: accountState.title,
    account_number: accountState.account_number,
    balance: accountState.account_balance,
    currency: accountState.currency,
    customer_id: null,
    facility_id: null,
    customers: accountState.customers,
    customer: accountState.customers[0],
  };

  async function confirmReopenAccount() {
    const accountResponse = await ReopenHouseAccount({ account_id: Number(accountId) }, true);

    if (accountResponse.status !== StatusCode.OK) {
      dispatch(showError(t("Error reopening account")));
      return;
    }
    dispatch(showSuccess(t("Successfully reopened account")));
    setAccountState(prevState => ({ ...prevState, reopenVisible: false }));
    void loadAccount();
  }

  return (
    <Page
      title={t("Account overview")}
      primaryAction={primaryAction}
      breadcrumbs={[
        { label: t("Settings"), url: "/admin/settings" },
        { label: t("Accounting"), url: "/admin/settings/accounts" },
        { label: t("Accounts"), url: backToAccountsUrl },
        { label: t("Account overview"), url: `/admin/settings/accounts/overview/${String(accountId)}` },
      ]}
      secondaryActions={accountState.closed ? [secondaryActions] : null}
    >
      <div className="account-overview-summary">
        <div className="account-overview-summary__identity">
          <div className="account-overview-summary__title-row">
            <div className="account-overview-summary__icon" aria-hidden>
              <FontAwesomeIcon icon={faHouse} />
            </div>
            <div className="account-overview-summary__title-copy">
              <span className="account-overview-summary__eyebrow">{t("Title")}</span>
              <div className="account-overview-summary__title-line">
                <h2 className="account-overview-summary__title">{accountState.title || "—"}</h2>
                {accountState.closed ? (
                  <Badge type="error" size="small" outline>
                    {t("Closed")}
                  </Badge>
                ) : (
                  <Badge type="success" size="small" outline>
                    {t("Active")}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          <div className="account-overview-summary__identity-divider" aria-hidden />

          <div className="account-overview-summary__customers">
            <span className="account-overview-summary__customers-label">
              {t("Customers")} ({accountState.customers.length})
            </span>
            {accountState.customers.length > 0 ? (
              <AccountCustomersCell account={customersAccount} visibleCount={OVERVIEW_VISIBLE_CUSTOMER_NAMES} />
            ) : (
              <span className="account-customers-cell__names">—</span>
            )}
          </div>
        </div>

        <div className="account-overview-summary__metrics">
          <div className="account-overview-summary__divider" aria-hidden />

          <div className="account-overview-summary__metric">
            <div className="account-overview-summary__metric-icon-wrap" aria-hidden>
              <FontAwesomeIcon icon={faHashtag} />
            </div>
            <div className="account-overview-summary__metric-copy">
              <span className="account-overview-summary__metric-label">{t("Account number")}</span>
              <span className="account-overview-summary__metric-value">{accountState.account_number || "—"}</span>
            </div>
          </div>

          <div className="account-overview-summary__divider" aria-hidden />

          <div className="account-overview-summary__metric">
            <div className="account-overview-summary__metric-icon-wrap" aria-hidden>
              <FontAwesomeIcon icon={faDollarSign} />
            </div>
            <div className="account-overview-summary__metric-copy">
              <span className="account-overview-summary__metric-label">{t("Balance")}</span>
              <span className="account-overview-summary__metric-value">
                <LocaleCurrency currency={accountState.currency} amount={accountState.account_balance} />
              </span>
            </div>
          </div>

          <div className="account-overview-summary__divider" aria-hidden />

          <div className="account-overview-summary__metric">
            <div className="account-overview-summary__metric-icon-wrap" aria-hidden>
              <FontAwesomeIcon icon={faCreditCard} />
            </div>
            <div className="account-overview-summary__metric-copy">
              <span className="account-overview-summary__metric-label">{t("Limit")}</span>
              <span className="account-overview-summary__metric-value">
                <LocaleCurrency currency={accountState.currency} amount={accountState.limit} />
              </span>
            </div>
          </div>
        </div>
      </div>

      <Tabs tabs={tableTabs} selected={selectedTableTab} onSelect={setSelectedTableTab}>
        <div style={{ display: selectedTableTab === 0 ? "block" : "none", paddingTop: "8px" }}>
          <DataTable<HouseAccountStatementType>
            columns={[
              { label: t("Statement") },
              { id: "start_date", label: t("Start Date") },
              { id: "end_date", label: t("End Date") },
              { label: t("Balance") },
              { label: "" },
              { label: "" },
            ]}
            loading={!accountState.account_statements}
            overflow
          >
            {accountState.account_statements?.map(statement => (
              <tr key={statement.id}>
                <td>{statement.id}</td>
                <td>{moment(statement.start_date).utc().format("LL")}</td>
                <td>{moment(statement.end_date).utc().format("LL")}</td>
                <td>
                  <LocaleCurrency currency={accountState.currency || "CAD"} amount={statement.current_balance} />
                </td>
                <td>
                  <p className="account-overview-action" onClick={() => downloadAccountStatement(statement.id)}>
                    {t("Download")}
                  </p>
                </td>
                <td>
                  <p
                    className="account-overview-action"
                    onClick={() =>
                      updateEmailStatementModal({
                        isOpen: true,
                        email: accountState.customer_email,
                        statementId: statement.id,
                      })
                    }
                  >
                    {t("Email")}
                  </p>
                </td>
              </tr>
            ))}
          </DataTable>
        </div>

        <div style={{ display: selectedTableTab === 1 ? "block" : "none", paddingTop: "8px" }}>
          <DataTable<HouseAccountTransactionType>
            columns={[
              { label: t("Date") },
              { label: t("Facility") },
              { label: t("Amount") },
              { label: t("Account") },
              { label: t("Payment Method") },
              { label: t("Last 4") },
              { label: "", width: "5%" },
            ]}
            overflow
            footer={{
              tableLimit: TABLE_LIMIT,
              tableOffset,
              handleTableOffset: handleTableNavigation,
              disableNextOffset: tableOffset + TABLE_LIMIT >= accountState.account_transactions.length,
            }}
          >
            {paginatedTransactions.map((transaction, index: number) => (
              <tr
                key={transaction.id}
                className={transaction.id === selectedParentId ? "house-account-transaction-selected-parent" : null}
                id={String(transaction.id)}
              >
                <td>{moment(transaction?.processed_at).format("ll")}</td>
                <td>{transaction?.facility?.long_name}</td>
                <td>
                  <span>
                    <LocaleCurrency amount={transaction?.amount} currency={transaction?.currency} />{" "}
                    {transaction?.currency?.toUpperCase()}
                  </span>
                </td>
                <td>
                  <p style={{ fontSize: "12px" }}>{valueToString(transaction?.kind)}</p>
                  <p>{transactionBadge(transaction?.kind, t)}</p>
                </td>
                <td>
                  <span>{valueToString(transaction?.payment_type) || ""}</span>
                </td>
                <td>{transaction.payment_last_4 ? transaction.payment_last_4 : ""}</td>
                <td>
                  <NavigationDropdownNew
                    rightAlign
                    position={index === 0 ? "bottom" : "top"}
                    showPlainTextLabel
                    label={<FontAwesomeIcon icon={["far", "chevron-down"]} />}
                    sections={[
                      [
                        {
                          label: t("Print"),
                          icon: "print",
                          onClick: () => getAccountTransactionReceipt(transaction?.id),
                          hidden: transaction?.kind !== "account_payment",
                        },
                        {
                          label: t("View Order"),
                          icon: "magnifying-glass",
                          onClick: () =>
                            history.push("/admin/order/" + String(transaction?.order_transaction?.order_id)),
                          disabled: !transaction.order_transaction?.order_id,
                        },
                        {
                          label: t("Highlight Original Transaction"),
                          icon: "eye",
                          onClick: () => setSelectedParentId(transaction.parent_id),
                          hidden: !transaction.parent_id,
                        },
                      ],
                    ]}
                    informationSection={transaction?.note ? [transaction.note] : undefined}
                  />
                </td>
              </tr>
            ))}
          </DataTable>
        </div>
      </Tabs>

      <Popup
        okText={t("Reopen")}
        onCancel={() => setAccountState(prevState => ({ ...prevState, reopenVisible: false }))}
        onOk={confirmReopenAccount}
        title={t("Reopen Account")}
        type="warning"
        description={t("Are you sure you want to reopen this account?")}
        open={accountState.reopenVisible}
      />

      <Portal isMounted={emailStatementModal.isOpen}>
        <Sheet
          title={t("Email Statement")}
          open={emailStatementModal?.isOpen}
          onCancel={closeEmailStatementModal}
          onOk={() => emailAccountStatement(emailStatementModal?.statementId)}
          okDisabled={!emailStatementModal?.email}
          okText={t("Send")}
          size="small"
        >
          <div className="p-1">
            <Input
              labelHidden
              onChange={e => updateEmailStatementModal({ email: e.target.value })}
              value={emailStatementModal?.email}
              placeholder={t("Email")}
            />
          </div>
        </Sheet>
      </Portal>
    </Page>
  );
}

function determineRefundedTransactions(transactions: HouseAccountTransactionType[]): HouseAccountTransactionType[] {
  const filteredTransactions = [...transactions].filter(transaction => transaction.kind !== "authorization");

  const markRefunds = filteredTransactions.reduce(
    (markedRefunds: HouseAccountTransactionType[], value: HouseAccountTransactionType, index: number) => {
      if (value.kind === "refund") {
        const parentTransactionIndex = markedRefunds.findIndex(transaction => transaction.id === value.parent_id);
        if (parentTransactionIndex >= 0) {
          markedRefunds[parentTransactionIndex] = { ...markedRefunds[parentTransactionIndex], refunded: true };
        }
        markedRefunds[index] = { ...markedRefunds[index], refunded: true };
      } else {
        markedRefunds[index] = { ...markedRefunds[index], refunded: false };
      }
      return markedRefunds;
    },
    [...filteredTransactions],
  );

  return markRefunds;
}

const transactionBadge = (type: TransactionKind, t: (key: string) => string) => {
  switch (type) {
    case "account_payment":
    case "refund":
    case "credit_adjustment":
      return (
        <Badge type="success" iconLeft={<FontAwesomeIcon icon={faArrowUpLeft} />}>
          {t("Credit")}
        </Badge>
      );
    case "debit_adjustment":
    case "sale":
      return (
        <Badge type="error" iconLeft={<FontAwesomeIcon icon={faArrowDownLeft} />}>
          {t("Debit")}
        </Badge>
      );
    default:
      return <></>;
  }
};
